<div class="portal-root">
  <div class="portal-container">

    <!-- ── 1. LOADING STATE ── -->
    @if (state === 'loading') {
      <mat-card class="cardWithShadow theme-card text-center p-y-48">
        <mat-card-content class="d-flex flex-column align-items-center justify-content-center gap-16">
          <mat-spinner diameter="44"></mat-spinner>
          <h3 class="f-s-20 f-w-700 m-b-0 text-dark">Loading your student profile…</h3>
          <p class="text-muted f-s-14 m-b-0">Please wait while we verify your access link.</p>
        </mat-card-content>
      </mat-card>
    }

    <!-- ── 2. ERROR / EXPIRED STATE ── -->
    @if (state === 'error') {
      <mat-card class="cardWithShadow theme-card text-center p-y-48">
        <mat-card-content class="d-flex flex-column align-items-center justify-content-center gap-16">
          <div class="p-16 rounded-circle bg-light-danger text-danger d-inline-flex align-items-center justify-content-center">
            <mat-icon class="f-s-36 width-36 height-36">link_off</mat-icon>
          </div>
          <h2 class="f-s-22 f-w-700 m-b-0 text-dark">Link Expired or Invalid</h2>
          <p class="text-muted f-s-14 m-b-0 max-w-400">
            This student portal link is no longer valid or has expired. You can request a fresh access link sent to your email.
          </p>
          <div class="m-t-8">
            <a mat-flat-button color="primary" routerLink="/portal-access-request">
              <mat-icon>send</mat-icon> Request a new link
            </a>
          </div>
        </mat-card-content>
      </mat-card>
    }

    <!-- ── 3. READY STATE: DEDICATED STUDENT PORTAL PAGE ── -->
    @if (state === 'ready' && student) {

      <!-- Top Branding Navigation Bar -->
      <div class="portal-top-bar d-flex align-items-center justify-content-between flex-wrap gap-12 m-b-20">
        <div class="d-flex align-items-center gap-12">
          @if (school?.logo) {
            <img [src]="school!.logo" [alt]="school!.name" class="school-logo-round" />
          } @else {
            <div class="school-logo-placeholder">
              <mat-icon>school</mat-icon>
            </div>
          }
          <div>
            <h3 class="f-s-18 f-w-700 m-b-2 text-dark">{{ school?.name || 'Student Portal' }}</h3>
            <div class="text-muted f-s-12">
              {{ school?.contactEmail || school?.notificationsEmail || '' }}
              {{ (school?.contactEmail && school?.phone) ? '•' : '' }}
              {{ school?.phone || '' }}
            </div>
          </div>
        </div>

        <div class="d-flex align-items-center gap-8">
          <span class="badge bg-light-success text-success f-s-12 f-w-600 p-x-12 p-y-6 rounded-pill d-inline-flex align-items-center gap-4">
            <mat-icon class="f-s-14 width-14 height-14">verified_user</mat-icon> Verified Student Portal
          </span>
        </div>
      </div>

      <!-- Multi-School Selector Bar (when registered in multiple schools) -->
      @if (availableSchools.length > 1) {
        <mat-card class="cardWithShadow theme-card m-b-20 school-selection-card">
          <mat-card-content class="p-16">
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-12 m-b-10">
              <span class="f-s-13 f-w-700 text-muted d-flex align-items-center gap-6">
                <mat-icon class="f-s-16 width-16 height-16 text-primary">domain</mat-icon>
                Select School to View Records ({{ availableSchools.length }} schools found):
              </span>
            </div>

            <div class="d-flex gap-10 flex-wrap">
              @for (s of availableSchools; track s.schoolId) {
                <button
                  type="button"
                  class="school-tab-chip"
                  [class.active]="s.schoolId === selectedSchoolId"
                  (click)="selectSchool(s.schoolId)"
                  [disabled]="switchingSchool"
                >
                  @if (s.schoolLogo) {
                    <img [src]="s.schoolLogo" [alt]="s.schoolName" class="school-chip-logo" />
                  } @else {
                    <mat-icon class="f-s-16 width-16 height-16">school</mat-icon>
                  }
                  <span class="school-chip-name">{{ s.schoolName }}</span>
                  @if (s.schoolId === selectedSchoolId) {
                    <mat-icon class="f-s-16 width-16 height-16 text-primary check-icon">check_circle</mat-icon>
                  }
                </button>
              }
            </div>
          </mat-card-content>
        </mat-card>
      }

      <!-- Switching school spinner -->
      @if (switchingSchool) {
        <div class="p-y-48 text-center">
          <mat-spinner diameter="36" class="mx-auto m-b-12"></mat-spinner>
          <div class="text-muted f-s-14">Updating records for selected school…</div>
        </div>
      } @else {

        <!-- ── STUDENT PERSONAL INFO HERO CARD ── -->
        <mat-card class="cardWithShadow theme-card m-b-20">
          <mat-card-content class="p-24">
            <!-- Student Header Profile -->
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-20 m-b-20">
              <div class="d-flex align-items-center gap-20">
                <div class="student-avatar-box">
                  @if (student.user?.image) {
                    <img [src]="student.user!.image" [alt]="fullName" class="student-avatar-img" />
                  } @else {
                    <span class="student-avatar-initials">{{ initials }}</span>
                  }
                </div>

                <div>
                  <div class="d-flex align-items-center gap-10 flex-wrap m-b-4">
                    <h2 class="f-s-24 f-w-700 m-b-0 text-dark">{{ fullName }}</h2>
                    <span class="badge {{ statusClass }} f-s-12 f-w-600 p-x-12 p-y-4 rounded-pill">
                      {{ statusLabel }}
                    </span>
                  </div>

                  <div class="text-muted f-s-14 d-flex align-items-center flex-wrap gap-12">
                    @if (email) {
                      <span class="d-inline-flex align-items-center gap-4">
                        <mat-icon class="f-s-16 width-16 height-16 text-muted">mail_outline</mat-icon> {{ email }}
                      </span>
                    }
                    @if (phone) {
                      <span>•</span>
                      <span class="d-inline-flex align-items-center gap-4">
                        <mat-icon class="f-s-16 width-16 height-16 text-muted">phone</mat-icon> {{ phone }}
                      </span>
                    }
                  </div>
                </div>
              </div>
            </div>

            <mat-divider class="m-y-16"></mat-divider>

            <!-- Student Personal Details 6-Grid -->
            <div class="row g-3">
              <div class="col-lg-3 col-md-6 col-12">
                <div class="info-tile">
                  <span class="info-tile-label">First Name</span>
                  <span class="info-tile-value">{{ student.firstName }}</span>
                </div>
              </div>

              <div class="col-lg-3 col-md-6 col-12">
                <div class="info-tile">
                  <span class="info-tile-label">Last Name</span>
                  <span class="info-tile-value">{{ student.secondName || '—' }}</span>
                </div>
              </div>

              <div class="col-lg-3 col-md-6 col-12">
                <div class="info-tile">
                  <span class="info-tile-label">Gender</span>
                  <span class="info-tile-value">{{ genderLabel || '—' }}</span>
                </div>
              </div>

              <div class="col-lg-3 col-md-6 col-12">
                <div class="info-tile">
                  <span class="info-tile-label">Nationality</span>
                  <span class="info-tile-value">{{ nationality || '—' }}</span>
                </div>
              </div>

              <div class="col-lg-6 col-md-6 col-12">
                <div class="info-tile">
                  <span class="info-tile-label">Residential Address</span>
                  <span class="info-tile-value">{{ address || '—' }}</span>
                </div>
              </div>

              <div class="col-lg-6 col-md-6 col-12">
                <div class="info-tile">
                  <span class="info-tile-label">Enrolled Date</span>
                  <span class="info-tile-value">{{ joinDate }}</span>
                </div>
              </div>
            </div>

            <!-- Guardian / Family Account Details (if linked) -->
            @if (student.guardian) {
              <div class="guardian-box m-t-16 p-16 rounded border bg-light-surface">
                <h4 class="f-s-14 f-w-700 m-b-10 text-dark d-flex align-items-center gap-6">
                  <mat-icon class="f-s-16 width-16 height-16 text-primary">family_restroom</mat-icon>
                  Guardian / Account Holder
                </h4>
                <div class="row g-2 f-s-13">
                  <div class="col-md-3 col-12">
                    <span class="text-muted">Guardian Name:</span>
                    <strong class="d-block text-dark">{{ guardianName }}</strong>
                  </div>
                  @if (student.guardian.email) {
                    <div class="col-md-3 col-12">
                      <span class="text-muted">Email:</span>
                      <strong class="d-block text-dark">{{ student.guardian.email }}</strong>
                    </div>
                  }
                  @if (student.guardian.phone) {
                    <div class="col-md-3 col-12">
                      <span class="text-muted">Phone:</span>
                      <strong class="d-block text-dark">{{ student.guardian.phone }}</strong>
                    </div>
                  }
                  @if (relationshipLabel) {
                    <div class="col-md-3 col-12">
                      <span class="text-muted">Relationship:</span>
                      <strong class="d-block text-dark">{{ relationshipLabel }}</strong>
                    </div>
                  }
                </div>
              </div>
            }
          </mat-card-content>
        </mat-card>

        <!-- ── SCHOOL OVERVIEW & FINANCIAL STATS GRID ── -->
        @if (summary) {
          <div class="row g-3 m-b-20">
            <!-- Completed Classes Metric -->
            <div class="col-lg-3 col-md-6 col-12">
              <mat-card class="cardWithShadow theme-card kpi-metric-card">
                <mat-card-content class="p-20 d-flex align-items-center gap-16">
                  <div class="kpi-icon bg-light-primary text-primary">
                    <mat-icon>school</mat-icon>
                  </div>
                  <div>
                    <div class="f-s-20 f-w-800 text-dark">{{ summary.completedClasses }} / {{ summary.totalClasses }}</div>
                    <div class="text-muted f-s-13">Classes Completed</div>
                  </div>
                </mat-card-content>
              </mat-card>
            </div>

            <!-- Total Billed Metric -->
            <div class="col-lg-3 col-md-6 col-12">
              <mat-card class="cardWithShadow theme-card kpi-metric-card">
                <mat-card-content class="p-20 d-flex align-items-center gap-16">
                  <div class="kpi-icon bg-light-info text-info">
                    <mat-icon>receipt</mat-icon>
                  </div>
                  <div>
                    <div class="f-s-20 f-w-800 text-dark">
                      {{ summary.totalCharged | currency: summary.currency : 'symbol' : '1.0-2' }}
                    </div>
                    <div class="text-muted f-s-13">Total Billed</div>
                  </div>
                </mat-card-content>
              </mat-card>
            </div>

            <!-- Total Paid Metric -->
            <div class="col-lg-3 col-md-6 col-12">
              <mat-card class="cardWithShadow theme-card kpi-metric-card">
                <mat-card-content class="p-20 d-flex align-items-center gap-16">
                  <div class="kpi-icon bg-light-success text-success">
                    <mat-icon>payments</mat-icon>
                  </div>
                  <div>
                    <div class="f-s-20 f-w-800 text-success">
                      {{ summary.totalPaid | currency: summary.currency : 'symbol' : '1.0-2' }}
                    </div>
                    <div class="text-muted f-s-13">Total Paid</div>
                  </div>
                </mat-card-content>
              </mat-card>
            </div>

            <!-- Outstanding / Credit Balance Metric -->
            <div class="col-lg-3 col-md-6 col-12">
              <mat-card class="cardWithShadow theme-card kpi-metric-card">
                <mat-card-content class="p-20 d-flex align-items-center gap-16">
                  <div
                    class="kpi-icon"
                    [class.bg-light-danger]="pendingBalances.length > 0"
                    [class.text-danger]="pendingBalances.length > 0"
                    [class.bg-light-success]="pendingBalances.length === 0"
                    [class.text-success]="pendingBalances.length === 0"
                  >
                    <mat-icon>{{ pendingBalances.length > 0 ? 'account_balance' : 'check_circle' }}</mat-icon>
                  </div>
                  <div>
                    @if (pendingBalances.length > 0) {
                      <div class="f-s-20 f-w-800 text-danger">
                        {{ pendingBalances[0].balance | currency: pendingBalances[0].currency : 'symbol' : '1.0-2' }}
                      </div>
                      <div class="text-muted f-s-13">Outstanding Due</div>
                    } @else if (creditBalances.length > 0) {
                      <div class="f-s-20 f-w-800 text-success">
                        +{{ creditBalances[0].amount | currency: creditBalances[0].currency : 'symbol' : '1.0-2' }}
                      </div>
                      <div class="text-muted f-s-13">Credit Balance (Advance)</div>
                    } @else {
                      <div class="f-s-20 f-w-800 text-success">
                        {{ 0 | currency: summary.currency : 'symbol' : '1.0-2' }}
                      </div>
                      <div class="text-muted f-s-13">Balance Settled</div>
                    }
                  </div>
                </mat-card-content>
              </mat-card>
            </div>
          </div>
        }
        <!-- ── TABBED DATA SECTIONS (CLASSES, BOOKINGS, TRANSACTIONS, LOCKERS) ── -->
        <mat-card class="cardWithShadow theme-card">
          <mat-card-content class="p-0">
            <mat-tab-group mat-stretch-tabs="false" animationDuration="0ms" class="theme-tabs">

              <!-- ── TAB 1: CLASSES & SCHEDULE ── -->
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="d-flex align-items-center gap-6">
                    <mat-icon class="f-s-18 width-18 height-18">schedule</mat-icon>
                    <span>Classes & Schedule</span>
                    <span class="tab-badge bg-light-primary text-primary">{{ classes.length }}</span>
                  </div>
                </ng-template>

                <div class="p-24">
                  <!-- Filter Pill Buttons -->
                  <div class="d-flex align-items-center justify-content-between flex-wrap gap-12 m-b-20">
                    <div class="d-flex gap-8 flex-wrap">
                      <button
                        type="button"
                        mat-stroked-button
                        [class.mat-flat-button]="classFilter === 'all'"
                        [color]="classFilter === 'all' ? 'primary' : undefined"
                        class="filter-pill-btn"
                        (click)="classFilter = 'all'"
                      >
                        All Classes ({{ classes.length }})
                      </button>
                      <button
                        type="button"
                        mat-stroked-button
                        [class.mat-flat-button]="classFilter === 'upcoming'"
                        [color]="classFilter === 'upcoming' ? 'primary' : undefined"
                        class="filter-pill-btn"
                        (click)="classFilter = 'upcoming'"
                      >
                        Upcoming ({{ summary?.upcomingClasses || 0 }})
                      </button>
                      <button
                        type="button"
                        mat-stroked-button
                        [class.mat-flat-button]="classFilter === 'completed'"
                        [color]="classFilter === 'completed' ? 'primary' : undefined"
                        class="filter-pill-btn"
                        (click)="classFilter = 'completed'"
                      >
                        Completed ({{ summary?.completedClasses || 0 }})
                      </button>
                    </div>
                  </div>

                  @if (filteredClasses.length === 0) {
                    <div class="text-center p-y-40 text-muted">
                      <mat-icon class="f-s-36 width-36 height-36 text-muted m-b-8">event_busy</mat-icon>
                      <h4 class="f-s-16 f-w-600 m-b-4">No Classes Found</h4>
                      <p class="f-s-13 text-muted m-b-0">There are no classes recorded under this filter for {{ school?.name }}.</p>
                    </div>
                  } @else {
                    <div class="d-flex flex-column gap-12">
                      @for (cls of filteredClasses; track cls.id) {
                        <div class="class-card-item p-16 rounded border bg-white shadow-xs">
                          <div class="d-flex align-items-center justify-content-between flex-wrap gap-16">
                            <div class="d-flex align-items-center gap-16">
                              <!-- Time Block -->
                              <div class="time-block p-10 rounded bg-light-primary text-center">
                                <div class="f-s-12 f-w-700 text-primary">{{ formatDateOnly(cls.startTime) }}</div>
                                <div class="f-s-14 f-w-800 text-dark">{{ formatTimeOnly(cls.startTime) }} - {{ formatTimeOnly(cls.endTime) }}</div>
                              </div>

                              <!-- Class Info -->
                              <div>
                                <div class="d-flex align-items-center gap-8 flex-wrap m-b-4">
                                  <h4 class="f-s-16 f-w-700 m-b-0 text-dark">{{ cls.activityName }}</h4>
                                  @if (cls.levelName) {
                                    <span class="svc-meta-pill">{{ cls.levelName }}</span>
                                  }
                                  @if (cls.modalityName) {
                                    <span class="svc-meta-pill bg-light-info text-info">{{ cls.modalityName }}</span>
                                  }
                                </div>
                                <div class="text-muted f-s-13">
                                  {{ cls.productTitle || 'Lesson' }}
                                  @if (cls.instructorName) {
                                    <span> • Instructor: <strong>{{ cls.instructorName }}</strong></span>
                                  }
                                </div>
                              </div>
                            </div>

                            <!-- Status Badges -->
                            <div class="d-flex align-items-center gap-8 flex-wrap">
                              @if (cls.attendanceLabel) {
                                <span class="badge {{ getAttendanceClass(cls.attendance) }} f-s-12 f-w-600 p-x-10 p-y-4 rounded-pill">
                                  {{ cls.attendanceLabel }}
                                </span>
                              }
                              <span class="badge {{ getLessonStatusClass(cls.status) }} f-s-12 f-w-600 p-x-10 p-y-4 rounded-pill">
                                {{ cls.statusLabel }}
                              </span>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  }
                </div>
              </mat-tab>

              <!-- ── TAB 2: COURSE BOOKINGS ── -->
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="d-flex align-items-center gap-6">
                    <mat-icon class="f-s-18 width-18 height-18">book</mat-icon>
                    <span>Course Bookings</span>
                    <span class="tab-badge bg-light-primary text-primary">{{ bookings.length }}</span>
                  </div>
                </ng-template>

                <div class="p-24">
                  @if (bookings.length === 0) {
                    <div class="text-center p-y-40 text-muted">
                      <mat-icon class="f-s-36 width-36 height-36 text-muted m-b-8">book_online</mat-icon>
                      <h4 class="f-s-16 f-w-600 m-b-4">No Bookings Found</h4>
                      <p class="f-s-13 text-muted m-b-0">No course enrollments found for {{ school?.name }}.</p>
                    </div>
                  } @else {
                    <div class="d-flex flex-column gap-16">
                      @for (b of bookings; track b.id) {
                        <div class="p-20 rounded border bg-white shadow-xs">
                          <div class="d-flex align-items-center justify-content-between flex-wrap gap-12 m-b-12">
                            <div>
                              <h4 class="f-s-18 f-w-700 m-b-4 text-dark">
                                {{ b.product?.title || b.activity?.name || 'Course Enrollment' }}
                              </h4>
                              <div class="d-flex align-items-center gap-8 flex-wrap">
                                @if (b.level?.levelName) {
                                  <span class="svc-meta-pill">{{ b.level!.levelName }}</span>
                                }
                                @if (b.modality?.modalityName) {
                                  <span class="svc-meta-pill bg-light-info text-info">{{ b.modality!.modalityName }}</span>
                                }
                                @if (b.hours) {
                                  <span class="svc-meta-pill bg-light text-muted">{{ b.hours }} Hours</span>
                                }
                              </div>
                            </div>

                            <div class="text-end">
                              <span class="badge bg-light-success text-success f-s-12 f-w-600 p-x-12 p-y-4 rounded-pill m-b-4 d-inline-block">
                                {{ b.statusLabel }}
                              </span>
                              @if (b.snapshotPrice != null) {
                                <div class="f-s-16 f-w-800 text-dark">
                                  {{ b.snapshotPrice | currency: b.currency : 'symbol' : '1.2-2' }}
                                </div>
                              }
                            </div>
                          </div>

                          <mat-divider class="m-y-12"></mat-divider>

                          <div class="d-flex align-items-center justify-content-between flex-wrap gap-12 f-s-13 text-muted">
                            <div>
                              @if (b.arrivalDate) {
                                <span>Arrival: <strong>{{ formatDateOnly(b.arrivalDate) }}</strong></span>
                              }
                              @if (b.departureDate) {
                                <span> • Departure: <strong>{{ formatDateOnly(b.departureDate) }}</strong></span>
                              }
                            </div>
                            <div>
                              <span>Lessons Progress: <strong>{{ b.completedLessonsCount }} of {{ b.lessonsCount }} completed</strong></span>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  }
                </div>
              </mat-tab>

              <!-- ── TAB 3: PAYMENT HISTORY & TRANSACTIONS ── -->
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="d-flex align-items-center gap-6">
                    <mat-icon class="f-s-18 width-18 height-18">receipt_long</mat-icon>
                    <span>Payment History</span>
                    <span class="tab-badge bg-light-primary text-primary">{{ transactions.length }}</span>
                  </div>
                </ng-template>

                <div class="p-24">
                  @if (transactions.length === 0) {
                    <div class="text-center p-y-40 text-muted">
                      <mat-icon class="f-s-36 width-36 height-36 text-muted m-b-8">receipt</mat-icon>
                      <h4 class="f-s-16 f-w-600 m-b-4">No Payment Records</h4>
                      <p class="f-s-13 text-muted m-b-0">No transaction records registered for this student in this school.</p>
                    </div>
                  } @else {
                    <div class="table-responsive rounded border">
                      <table mat-table [dataSource]="transactions" class="w-100">
                        <ng-container matColumnDef="createdAt">
                          <th mat-header-cell *matHeaderCellDef class="f-w-700 f-s-13">Date</th>
                          <td mat-cell *matCellDef="let t" class="f-s-13 text-muted">
                            {{ formatDateTime(t.createdAt) }}
                          </td>
                        </ng-container>

                        <ng-container matColumnDef="typeName">
                          <th mat-header-cell *matHeaderCellDef class="f-w-700 f-s-13">Type</th>
                          <td mat-cell *matCellDef="let t">
                            <span class="badge {{ t.direction === 1 ? 'bg-light-success text-success' : 'bg-light-primary text-primary' }} f-s-11 f-w-600 p-x-8 p-y-3 rounded-pill">
                              {{ t.typeName }}
                            </span>
                          </td>
                        </ng-container>

                        <ng-container matColumnDef="forItem">
                          <th mat-header-cell *matHeaderCellDef class="f-w-700 f-s-13">Description</th>
                          <td mat-cell *matCellDef="let t" class="f-s-14 f-w-600 text-dark">
                            {{ t.forItem }}
                          </td>
                        </ng-container>

                        <ng-container matColumnDef="invoiceIdentifier">
                          <th mat-header-cell *matHeaderCellDef class="f-w-700 f-s-13">Invoice / Ref</th>
                          <td mat-cell *matCellDef="let t" class="f-s-13 text-muted">
                            {{ t.invoiceIdentifier || '—' }}
                          </td>
                        </ng-container>

                        <ng-container matColumnDef="amount">
                          <th mat-header-cell *matHeaderCellDef class="f-w-700 f-s-13 text-end">Amount</th>
                          <td mat-cell *matCellDef="let t" class="text-end">
                            <span [class.text-success]="t.direction === 1" [class.text-dark]="t.direction !== 1" class="f-w-700 f-s-14">
                              {{ t.direction === 1 ? '+' : '-' }}{{ t.amount | currency: t.currency : 'symbol' : '1.2-2' }}
                            </span>
                          </td>
                        </ng-container>

                        <tr mat-header-row *matHeaderRowDef="transactionColumns" class="bg-light"></tr>
                        <tr mat-row *matRowDef="let row; columns: transactionColumns;"></tr>
                      </table>
                    </div>
                  }
                </div>
              </mat-tab>

              <!-- ── TAB 4: LOCKERS & EQUIPMENT (if any) ── -->
              @if (boxBookings.length > 0 || rentals.length > 0) {
                <mat-tab>
                  <ng-template mat-tab-label>
                    <div class="d-flex align-items-center gap-6">
                      <mat-icon class="f-s-18 width-18 height-18">inventory_2</mat-icon>
                      <span>Lockers & Gear</span>
                      <span class="tab-badge bg-light-primary text-primary">{{ boxBookings.length + rentals.length }}</span>
                    </div>
                  </ng-template>

                  <div class="p-24">
                    <!-- Lockers -->
                    @if (boxBookings.length > 0) {
                      <h4 class="f-s-16 f-w-700 m-b-12 text-dark d-flex align-items-center gap-6">
                        <mat-icon class="text-primary f-s-18 width-18 height-18">lock</mat-icon> Storage Lockers
                      </h4>
                      <div class="d-flex flex-column gap-12 m-b-24">
                        @for (box of boxBookings; track box.id) {
                          <div class="p-16 rounded border bg-white shadow-xs d-flex align-items-center justify-content-between flex-wrap gap-16">
                            <div class="d-flex align-items-center gap-16">
                              <div class="p-12 rounded bg-light-info text-info d-flex align-items-center justify-content-center">
                                <mat-icon>inventory_2</mat-icon>
                              </div>
                              <div>
                                <h4 class="f-s-16 f-w-700 m-b-2 text-dark">{{ box.boxLabel || 'Storage Locker' }}</h4>
                                <div class="text-muted f-s-13">
                                  {{ box.zoneName || 'Main Zone' }} • {{ box.sizeName || 'Standard' }}
                                </div>
                                <div class="text-muted f-s-12 m-t-4">
                                  {{ formatDateOnly(box.startDate) }} - {{ formatDateOnly(box.endDate) }}
                                </div>
                              </div>
                            </div>
                            <span class="badge bg-light-success text-success f-s-12 f-w-600 p-x-12 p-y-4 rounded-pill">
                              {{ box.statusLabel }}
                            </span>
                          </div>
                        }
                      </div>
                    }

                    <!-- Rentals -->
                    @if (rentals.length > 0) {
                      <h4 class="f-s-16 f-w-700 m-b-12 text-dark d-flex align-items-center gap-6">
                        <mat-icon class="text-warning f-s-18 width-18 height-18">surfing</mat-icon> Equipment Rentals
                      </h4>
                      <div class="d-flex flex-column gap-12">
                        @for (r of rentals; track r.id) {
                          <div class="p-16 rounded border bg-white shadow-xs d-flex align-items-center justify-content-between flex-wrap gap-16">
                            <div class="d-flex align-items-center gap-16">
                              <div class="p-12 rounded bg-light-warning text-warning d-flex align-items-center justify-content-center">
                                <mat-icon>surfing</mat-icon>
                              </div>
                              <div>
                                <h4 class="f-s-16 f-w-700 m-b-2 text-dark">{{ r.productName }}</h4>
                                <div class="text-muted f-s-12">
                                  {{ formatDateTime(r.startDate) }} - {{ formatDateTime(r.endDate) }}
                                </div>
                              </div>
                            </div>
                            <span class="badge bg-light-primary text-primary f-s-12 f-w-600 p-x-12 p-y-4 rounded-pill">
                              {{ r.statusLabel }}
                            </span>
                          </div>
                        }
                      </div>
                    }
                  </div>
                </mat-tab>
              }

            </mat-tab-group>
          </mat-card-content>
        </mat-card>

      }
    }
  </div>
</div>
